import { useAppStore } from "@hooks/useAppStore";
import type { BaseItemSchema } from "@memorang/types";
import { useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import type { ItemFilterType } from "./types";
import type { ItemFilter } from "./types";

const useItemsFilter = (sessionItems?: BaseItemSchema[]) => {
	const [selectedFilters, setSelectedFilters] = useState<ItemFilterType[]>([
		"all",
	]);
	const { id: numericId, examName } = useLocalSearchParams<{
		id: string;
		examName: string;
	}>();
	const practiceTestLabel = useAppStore(
		(state) => state.exam.practiceTest?.label,
	);

	const [localItems, setLocalItems] = useState<BaseItemSchema[]>();
	const filters: ItemFilter[] = [
		{
			label: "All Items",
			// icon: "filter-variant" as const,
			type: "all",
			tooltip: "Filter by all items",
			selected: selectedFilters.includes("all"),
		},
		{
			label: "Correct",
			// icon: "check-circle-outline" as const,
			type: "correct",
			tooltip: "Filter by correct items",
			selected: selectedFilters.includes("correct"),
		},
		{
			label: "Incorrect",
			// icon: "close-circle-outline" as const,
			type: "incorrect",
			tooltip: "Filter by incorrect items",
			selected: selectedFilters.includes("incorrect"),
		},
		{
			label: "Marked",
			// icon: "close-circle-outline" as const,
			type: "marked",
			tooltip: "Filter by flagged items",
			selected: selectedFilters.includes("marked"),
		},
	];

	const filterItems = useCallback(() => {
		if (selectedFilters.includes("all")) {
			setLocalItems(sessionItems);
			return;
		}

		const filteredItems = sessionItems?.filter((item) => {
			if (selectedFilters.includes("correct") && item.answer?.correct) {
				return true;
			}
			if (
				selectedFilters.includes("incorrect") &&
				item.answer &&
				!item.answer.correct
			) {
				return true;
			}
			if (selectedFilters.includes("marked") && item.answer?.marked) {
				return true;
			}
			return false;
		});

		setLocalItems(filteredItems);
	}, [selectedFilters, sessionItems]);

	useEffect(() => {
		filterItems();
	}, [filterItems]);

	const handleSelectFilter = (filter: ItemFilterType) => {
		if (filter === "all") {
			setSelectedFilters(["all"]);
		} else if (selectedFilters.includes(filter)) {
			const newFilters = selectedFilters.filter((f) => f !== filter);
			if (newFilters.length === 0) {
				setSelectedFilters(["all"]);
			} else {
				setSelectedFilters(newFilters);
			}
		} else {
			const newFilters = selectedFilters.filter((f) => f !== "all");
			setSelectedFilters([...newFilters, filter]);
		}
	};

	const handleClearFilters = () => {
		setSelectedFilters(["all"]);
	};

	const isProgressPage = window.location?.pathname.includes("progress");
	const fromStudyPack = window.location?.pathname.includes("study-pack");

	const breadcrumbs = [
		...(examName
			? [
					{
						label: practiceTestLabel ?? "Practice Tests",
						href: "/practice-tests",
					},
					{
						label: examName,
					},
					{
						label: numericId ? `Test #${numericId}` : "Report",
						href: `/session/${numericId}/report/`,
					},
				]
			: []),
		...(isProgressPage
			? [
					{
						label: "Progress",
						href: "/progress",
					},
					...(fromStudyPack
						? [{ label: "Study Pack" }]
						: [
								{
									label: "Practice Tests",
								},
							]),
				]
			: []),
	];

	return {
		filters,
		selectedFilters,
		handleSelectFilter,
		handleClearFilters,
		localItems,
		breadcrumbs,
	};
};

export default useItemsFilter;
